import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [creditsAdded, setCreditsAdded] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await axios.get(
        `${API}/payments/checkout/status/${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.payment_status === "paid") {
        setStatus("success");
        setCreditsAdded(response.data.credits_added || 0);
        
        // Update user data in localStorage
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          user.credits = (user.credits || 0) + (response.data.credits_added || 0);
          localStorage.setItem("user", JSON.stringify(user));
        }
        return;
      } else if (response.data.status === "expired") {
        setStatus("error");
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);

    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("error");
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin" />
            </div>
            <CardTitle className="text-2xl">Zahlung wird überprüft...</CardTitle>
            <CardDescription>Bitte warten Sie einen Moment</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <CheckCircle className="w-16 h-16 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-700">Zahlung erfolgreich!</CardTitle>
            <CardDescription className="text-lg mt-2">
              Vielen Dank für Ihren Kauf
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-center text-green-800 font-semibold text-lg">
                {creditsAdded} Credits wurden Ihrem Konto hinzugefügt! 🎉
              </p>
            </div>
            <Button
              onClick={() => navigate("/")}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              Zurück zur Startseite
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <XCircle className="w-16 h-16 text-red-600" />
          </div>
          <CardTitle className="text-2xl text-red-700">Fehler bei der Zahlung</CardTitle>
          <CardDescription className="text-base mt-2">
            Die Zahlung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => navigate("/")}
            className="w-full"
            variant="outline"
          >
            Zurück zur Startseite
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
